import type { Review } from '../../store/selectors'
import { domainLabel } from '../../lib/rubric'
import { formatRelative } from '../../lib/dates'

/**
 * One week's check-in, both halves: what the parent said (ratings, answers,
 * win, adjust) and what Sapling made of it (moved, stuck, changes).
 */
const GLYPH: Record<number, string> = { [-1]: '−', 0: '=', 1: '+', 2: '++' }

export function ReviewCard({ review, weekLabel }: { review: Review; weekLabel?: string }) {
  const p = review.parent
  const a = review.agent
  return (
    <article className="review">
      <header className="review-head">
        <span className="review-title">{weekLabel ?? `${review.period} of ${review.date}`}</span>
        <span className="review-when muted">
          {a ? `Reviewed ${formatRelative(a.generatedAt)}` : p?.submittedAt ? `Sent ${formatRelative(p.submittedAt)} · Sapling is reviewing` : 'Open'}
        </span>
      </header>

      {p?.ratings && p.ratings.length > 0 && (
        <ul className="review-ratings">
          {p.ratings.map((r) => (
            <li key={r.domainKey} className={`review-rating rate-${r.rating < 0 ? 'neg' : r.rating}`}>
              <span className="review-rating-glyph">{GLYPH[r.rating] ?? r.rating}</span>
              <span>{domainLabel(r.domainKey)}</span>
            </li>
          ))}
        </ul>
      )}
      {(p?.win || p?.adjust) && (
        <dl className="review-parent">
          {p?.win && <><dt>Biggest win</dt><dd>{p.win}</dd></>}
          {p?.adjust && <><dt>Adjust</dt><dd>{p.adjust}</dd></>}
        </dl>
      )}
      {p?.answers && p.answers.length > 0 && (
        <details className="review-answers">
          <summary>{p.answers.length} answer{p.answers.length === 1 ? '' : 's'}</summary>
          <dl className="review-parent">
            {p.answers.map((x) => (<div key={x.question}><dt>{x.question}</dt><dd>{x.answer}</dd></div>))}
          </dl>
        </details>
      )}

      {a && (
        <div className="review-agent">
          <p className="review-summary">{a.summary}</p>
          {a.moved?.length ? <List title="Moved" items={a.moved} tone="up" /> : null}
          {a.stuck?.length ? <List title="Stuck" items={a.stuck} tone="stall" /> : null}
          {a.changes?.length ? <List title="Next week" items={a.changes} tone="plan" /> : null}
        </div>
      )}
    </article>
  )
}

function List({ title, items, tone }: { title: string; items: string[]; tone: string }) {
  return (
    <div className={`review-list is-${tone}`}>
      <span className="review-list-title">{title}</span>
      <ul>{items.map((x, i) => <li key={i}>{x}</li>)}</ul>
    </div>
  )
}
